import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Header } from './components/Header';
import { Home } from './pages/Home';
import { Feed } from './pages/Feed';
import { PhotoDetail } from './pages/PhotoDetail';
import { Players } from './pages/Players';
import { PlayerProfile } from './pages/PlayerProfile';
import { Rooms } from './pages/Rooms';
import { Join } from './pages/Join';
import { NotFound } from './pages/NotFound';
import { SignIn } from './pages/SignIn';
import { clearSession, getToken, getUsername, onAuthChange } from './lib/auth';

// Routes reachable WITHOUT a session. /join is the pre-account flow — a
// player redeeming a signup code has no credentials yet, so gating it would
// make new accounts impossible to create. It is [AllowAnonymous] server-side
// for the same reason.
const PUBLIC_PATHS = ['/join'];

export function App() {
  const location = useLocation();
  const [token, setToken] = useState<string | null>(getToken());

  // Re-render on sign-in and on the 401-triggered clearSession in api.ts, so
  // an expired session drops straight back to the gate instead of leaving
  // pages rendering half-empty.
  useEffect(() => onAuthChange(() => setToken(getToken())), []);

  const isPublic = PUBLIC_PATHS.some(
    p => location.pathname === p || location.pathname.startsWith(p + '/'));

  // The gate. This only hides the UI — the real enforcement is [Authorize]
  // on PublicSiteController, which is what actually stops the data being
  // read. Both matter: without the server side this would be a curtain
  // rather than a lock, since the API answers on every hostname.
  if (!token && !isPublic) {
    return (
      <Shell>
        <Routes>
          <Route path="/join" element={<Join />} />
          <Route path="*" element={<SignIn />} />
        </Routes>
      </Shell>
    );
  }

  return (
    <Shell signedInAs={getUsername()}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/photo/:id" element={<PhotoDetail />} />
        <Route path="/players" element={<Players />} />
        <Route path="/players/:id" element={<PlayerProfile />} />
        <Route path="/rooms" element={<Rooms />} />
        <Route path="/join" element={<Join />} />
        {/* Legacy paths from the vanilla-JS feed.localhost site */}
        <Route path="/p/:id" element={<RedirectPhoto />} />
        <Route path="/u/:id" element={<RedirectPlayer />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}

function Shell({ children, signedInAs }: { children: React.ReactNode; signedInAs?: string | null }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 mx-auto w-full max-w-6xl px-4 sm:px-6 py-6">{children}</main>
      <footer className="border-t border-ink-800 text-xs text-ink-400">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-2">
          {/* AGPL-3.0 attribution. NOTICE clause 3 requires the upstream
              credit somewhere visible and names a web-facing footer as an
              acceptable placement. Renaming the fork to Tsunami is allowed;
              this credit and the NOTICE file are what must be preserved.
              Do not remove. */}
          <span>
            RR Tsunami — an independent private multiplayer service. Based on{' '}
            <a className="underline hover:text-ink-200" href="https://github.com/Alexa-RR/Dorknet" target="_blank" rel="noreferrer">DorkNet</a>{' '}
            by Alexa, licensed AGPL-3.0.
          </span>
          <span>
            {signedInAs && (
              <>
                <span>{signedInAs}</span>
                <span className="mx-2">·</span>
                <button className="hover:text-ink-200 underline" onClick={clearSession}>Sign out</button>
                <span className="mx-2">·</span>
              </>
            )}
            {/* The "Admin" link was REMOVED 2026-08-04. The portal moved off
                the obvious hostname deliberately; linking it from a public
                page would have advertised it straight back. */}
            <a className="hover:text-ink-200" href="/privacy/">Privacy</a>
          </span>
        </div>
      </footer>
    </div>
  );
}

// Small redirect shims so old #/p/123 / #/u/45 links keep working.
function RedirectPhoto()  { const { id } = useParams(); return <Navigate to={`/photo/${id}`}  replace />; }
function RedirectPlayer() { const { id } = useParams(); return <Navigate to={`/players/${id}`} replace />; }
